import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import axios from "axios";
import { assignFundSource } from "../assignFundSource";
import { useStageGuard } from "../useStageGuard";
import { usePreventBack } from "../usePreventBack";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import styles from "../index.module.css";

const INDIAN_STATES = [
  "JAMMU & KASHMIR",
  "HIMACHAL PRADESH",
  "PUNJAB",
  "CHANDIGARH",
  "UTTARANCHAL",
  "HARYANA",
  "DELHI",
  "RAJASTHAN",
  "UTTAR PRADESH",
  "BIHAR",
  "SIKKIM",
  "ARUNACHAL PRADESH",
  "NAGALAND",
  "MANIPUR",
  "MIZORAM",
  "TRIPURA",
  "MEGHALAYA",
  "ASSAM",
  "WEST BENGAL",
  "JHARKHAND",
  "ORISSA",
  "CHATTISGARH",
  "MADHYA PRADESH",
  "GUJARAT",
  "DAMAN & DIU",
  "DADRA & NAGAR HAVELI",
  "MAHARASHTRA",
  "ANDHRA PRADESH",
  "KARNATAKA",
  "GOA",
  "LAKSHADWEEP",
  "KERALA",
  "TAMIL NADU",
  "PONDICHERRY",
  "ANDAMAN AND NICOBAR ISLANDS",
  "TELANGANA",
  "LADAKH",
  "APO Address",
];

const matchState = (val: string | null | undefined): string => {
  if (!val) return "";
  const upper = val.toUpperCase();
  return INDIAN_STATES.find((s) => s.toUpperCase() === upper) || "";
};

const emptyForm = {
  currentAddress: "",
  currentCity: "",
  currentState: "",
  currentPincode: "",
  permanentAddress: "",
  permanentCity: "",
  permanentState: "",
  permanentPincode: "",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.72rem 0.9rem",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontSize: "0.88rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Outfit, sans-serif",
  color: "#333",
  background: "#fff",
};

const labelStyle: React.CSSProperties = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: "#999",
  letterSpacing: "0.7px",
  textTransform: "uppercase",
  marginBottom: "0.3rem",
  display: "block",
};

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
}

function sanitizeAddress(address: string, state = "", city = ""): string {
  if (!address) return "";
  const currentState = state.toLowerCase().trim();
  const currentCity = city.toLowerCase().trim();

  address = address.replace(/\b[1-9][0-9]{2}[-\s]?[0-9]{3}\b/g, "").trim();

  const components = address
    .split(",")
    .map((c) => c.trim())
    .filter((c) => {
      const lower = c.toLowerCase();
      return c.length > 4 && lower !== currentState && lower !== currentCity;
    });

  const seen = new Set<string>();
  const deduped = components.filter((c) => {
    const key = c.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const withoutSubstrings = deduped.filter((comp, i) =>
    !deduped.some(
      (other, j) =>
        i !== j &&
        other.toLowerCase().includes(comp.toLowerCase()) &&
        other.length > comp.length
    )
  );

  const fuzzyDeduped: string[] = [];
  for (const comp of withoutSubstrings) {
    const compLower = comp.toLowerCase();
    const isDup = fuzzyDeduped.some((kept) => {
      const keptLower = kept.toLowerCase();
      const maxLen = Math.max(compLower.length, keptLower.length);
      if (maxLen < 6) return false;
      return levenshtein(compLower, keptLower) / maxLen <= 0.15;
    });
    if (!isDup) fuzzyDeduped.push(comp);
  }

  return fuzzyDeduped
    .map((comp) =>
      comp
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ")
    )
    .join(", ");
}

function EligibilityAddressDetails() {
  usePreventBack();
  const navigate = useNavigate();

  const userData = useEligibilityData();

  useStageGuard(userData?.userId, userData?.applicationId, 10);

  const [form, setForm] = useState(emptyForm);
  const [initialForm, setInitialForm] = useState(emptyForm);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [stateOpen, setStateOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const stateSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target as Node)) {
        setStateOpen(false);
        setStateSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (userData?.userId && userData?.applicationId) {
      fetchSummary(userData.userId, userData.applicationId);
    }
  }, []);

  const fetchSummary = async (userId: string, applicationId: string) => {
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userId}&applicationId=${applicationId}`,
        { headers }
      );
      const result = await response.json();
      const d = result?.data;
      if (d) {
        setSummaryData(d);
        const prefilled = {
          currentAddress: d.currentAddress || "",
          currentCity: d.currentCity || "",
          currentState: matchState(d.currentState),
          currentPincode: d.currentPincode || "",
          permanentAddress: sanitizeAddress(d.permanentAddress || "", d.permanentState || "", d.permanentCity || ""),
          permanentCity: d.permanentCity || "",
          permanentState: matchState(d.permanentState),
          permanentPincode: d.permanentPincode || "",
        };
        setForm(prefilled);
        setInitialForm(prefilled);
      }
    } catch {
      // leave fields empty if fetch fails
    }
  };

  const handleChange = (field: string, value: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (sameAsPermanent && field.startsWith("permanent")) {
        const currentKey = field.replace("permanent", "current");
        updated[currentKey as keyof typeof emptyForm] = value;
      }
      return updated;
    });
  };

  const handleSameAsPermanent = (checked: boolean) => {
    setSameAsPermanent(checked);
    setFieldErrors({});
    if (checked) {
      setForm((prev) => ({
        ...prev,
        currentAddress: prev.permanentAddress,
        currentCity: prev.permanentCity,
        currentState: prev.permanentState,
        currentPincode: prev.permanentPincode,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        currentAddress: initialForm.currentAddress,
        currentCity: initialForm.currentCity,
        currentState: initialForm.currentState,
        currentPincode: initialForm.currentPincode,
      }));
    }
  };

  const checkAndNavigateNext = async (token: string | null) => {
    const appId = (userData as any)?.applicationId;
    const userId = (userData as any)?.userId;

    try {
      const summaryRes = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userId}&applicationId=${appId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const summaryJson = await summaryRes.json();
      const d = summaryJson?.data;
      const rawInstId = d?.instituteId;
      const instId = Array.isArray(rawInstId) ? rawInstId[0]?.id : rawInstId;
      const courseComplete = instId && d?.courseName?.trim() && d?.courseFees && d?.loanTenure;
      if (!courseComplete) {
        navigate("/eligibility/course-details", { replace: true, state: { data: userData } });
        return;
      }
      if (!d?.email?.trim()) {
        navigate("/eligibility/email", { replace: true, state: { data: userData } });
        return;
      }
      assignFundSource(userData, sessionStorage.getItem("eligibility_auth_token"));
      navigate("/eligibility/evaluating", { replace: true, state: { data: userData, email: d.email } });
      return;
    } catch {}

    navigate("/eligibility/course-details", { replace: true, state: { data: userData } });
  };

  const navigateByStage = (stage: number, token: string | null) => {
    if (stage === 5) { navigate("/eligibility/under-review", { replace: true, state: { data: userData } }); return; }
    if (stage === 8) { navigate("/eligibility/congratulations", { replace: true, state: { data: userData } }); return; }
    if (stage === 9) { navigate("/eligibility/selfie", { replace: true, state: { data: userData } }); return; }
    if (stage === 11) { navigate("/eligibility/agreement", { replace: true, state: { data: userData } }); return; }
    if (stage === 12) { navigate("/eligibility/emandate", { replace: true, state: { data: userData } }); return; }
    if (stage === 24) { navigate("/eligibility/digilocker", { replace: true, state: { data: userData } }); return; }
    if (stage === 25) { navigate("/eligibility/evaluating", { replace: true, state: { data: userData } }); return; }
    if (stage === 18) { navigate("/eligibility/rejected", { replace: true, state: { data: userData } }); return; }
    checkAndNavigateNext(token);
  };

  const fetchStageAndNavigate = async (token: string | null) => {
    const appId = summaryData?.applicationId || userData.applicationId;
    if (appId) {
      try {
        const stageRes = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${appId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const stageData = await stageRes.json();
        navigateByStage(stageData?.data?.stage, token);
        return;
      } catch {
        // fall through
      }
    }
    await checkAndNavigateNext(token);
  };

  const handleNext = async () => {
    setError("");

    const errors: Record<string, string> = {};
    if (!form.currentAddress?.trim())
      errors.currentAddress = "Please enter your street address.";
    if (!form.currentCity?.trim())
      errors.currentCity = "Please enter your city.";
    if (!form.currentPincode?.trim())
      errors.currentPincode = "Please enter your pincode.";
    else if (!/^\d{6}$/.test(form.currentPincode.trim()))
      errors.currentPincode = "Pincode must be exactly 6 digits.";
    if (!form.currentState?.trim())
      errors.currentState = "Please select your state.";

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const token = sessionStorage.getItem("eligibility_auth_token");

    const changed: Record<string, any> = {};
    (Object.keys(form) as (keyof typeof emptyForm)[]).forEach((key) => {
      if (form[key] !== initialForm[key]) changed[key] = form[key];
    });

    if (Object.keys(changed).length === 0) {
      await fetchStageAndNavigate(token);
      return;
    }

    setLoading(true);
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const payload = {
        applicationId: summaryData?.applicationId || userData.applicationId,
        userId: summaryData?.userId || userData.userId,
        ...changed,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_DASHBOARD_URL}/users/profile-details/create`,
        payload,
        { headers }
      );

      if (res.status === 200) {
        await fetchStageAndNavigate(token);
      } else {
        setError("Oops! There is an issue at our end. Please contact us.");
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(msg || "Oops! There is an issue at our end. Please contact us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Header ── */}
              <div className={styles.cardHeader}>
                <div style={{
                  width: "2.4rem", height: "2.4rem", borderRadius: "50%",
                  background: "#F4E5DD", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                    <circle cx="12" cy="9" r="2.5" stroke="#d32028" strokeWidth="1.5" />
                  </svg>
                </div>
                <p className={styles.cardTitle}>Confirm Address</p>
              </div>


              {/* ── Permanent Address ── */}
              <div style={{ marginBottom: "1.1rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginBottom: "0.65rem" }}>
                  <span style={{ fontSize: "1rem", fontWeight: 700, color: "#1a1a1a" }}>Permanent Address</span>
                  <span style={{
                    fontSize: "0.6rem", fontWeight: 700, color: "#d32028",
                    background: "#fde8e8", padding: "0.15rem 0.45rem",
                    borderRadius: "4px", letterSpacing: "0.5px", textTransform: "uppercase",
                  }}>
                    Digilocker Verified
                  </span>
                </div>

                {/* Read-only card — always non-editable */}
                <div style={{
                  background: "#f7f7f7", border: "1px solid #ebebeb",
                  borderRadius: "10px", padding: "0.85rem 1rem",
                }}>
                  {form.permanentAddress ? (
                    <>
                      <p style={{ margin: "0 0 0.15rem", fontSize: "0.88rem", fontWeight: 700, color: "#222" }}>
                        {form.permanentAddress}
                      </p>
                      {(form.permanentCity || form.permanentState) && (
                        <p style={{ margin: "0 0 0.1rem", fontSize: "0.83rem", color: "#888" }}>
                          {[form.permanentCity, form.permanentState].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {form.permanentPincode && (
                        <p style={{ margin: 0, fontSize: "0.83rem", color: "#888" }}>
                          {form.permanentPincode}
                        </p>
                      )}
                    </>
                  ) : (
                    <p style={{ margin: 0, fontSize: "0.83rem", color: "#bbb" }}>
                      Permanent Address
                    </p>
                  )}
                </div>

                {/* Same as Permanent Address toggle */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginTop: "0.85rem", padding: "0.1rem 0",
                }}>
                  <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#333" }}>
                    Same as Permanent Address
                  </span>
                  <div
                    onClick={() => handleSameAsPermanent(!sameAsPermanent)}
                    style={{
                      width: "2.8rem", height: "1.5rem", borderRadius: "1rem",
                      background: sameAsPermanent ? "#d32028" : "#ccc",
                      position: "relative", cursor: "pointer",
                      transition: "background 0.2s", flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: "absolute", top: "0.18rem",
                      left: sameAsPermanent ? "1.35rem" : "0.18rem",
                      width: "1.15rem", height: "1.15rem", borderRadius: "50%",
                      background: "#fff", transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                    }} />
                  </div>
                </div>
              </div>

              {/* ── Current Address ── */}
              <div>
                <p style={{ fontSize: "1rem", fontWeight: 700, color: "#1a1a1a", margin: "-0.5rem 0 0.75rem" }}>
                  Current Address
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                  <div>
                    <span style={labelStyle}>Door No. / Street Address</span>
                    <input
                      style={{ ...inputStyle, background: sameAsPermanent ? "#f5f5f5" : "#fff", borderColor: fieldErrors.currentAddress ? "#d32028" : "#e0e0e0" }}
                      placeholder="e.g. 101, Park Avenue"
                      value={form.currentAddress}
                      onChange={(e) => handleChange("currentAddress", e.target.value)}
                      disabled={sameAsPermanent}
                    />
                    {fieldErrors.currentAddress && (
                      <p style={{ color: "#d32028", fontSize: "0.75rem", margin: "0.25rem 0 0", fontFamily: "Outfit, sans-serif" }}>
                        {fieldErrors.currentAddress}
                      </p>
                    )}
                  </div>
                  {/* City + Pincode + State row */}
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <div style={{ flex: 2 }}>
                      <span style={labelStyle}>City</span>
                      <input
                        style={{ ...inputStyle, background: sameAsPermanent ? "#f5f5f5" : "#fff", borderColor: fieldErrors.currentCity ? "#d32028" : "#e0e0e0" }}
                        placeholder="City"
                        value={form.currentCity}
                        onChange={(e) => handleChange("currentCity", e.target.value)}
                        disabled={sameAsPermanent}
                      />
                      {fieldErrors.currentCity && (
                        <p style={{ color: "#d32028", fontSize: "0.75rem", margin: "0.25rem 0 0", fontFamily: "Outfit, sans-serif" }}>
                          {fieldErrors.currentCity}
                        </p>
                      )}
                    </div>
                    <div style={{ flex: 2 }}>
                      <span style={labelStyle}>Pincode</span>
                      <input
                        style={{ ...inputStyle, background: sameAsPermanent ? "#f5f5f5" : "#fff", borderColor: fieldErrors.currentPincode ? "#d32028" : "#e0e0e0" }}
                        placeholder="PIN"
                        value={form.currentPincode}
                        onChange={(e) => handleChange("currentPincode", e.target.value.replace(/\D/g, ""))}
                        disabled={sameAsPermanent}
                        maxLength={6}
                        inputMode="numeric"
                      />
                      {fieldErrors.currentPincode && (
                        <p style={{ color: "#d32028", fontSize: "0.75rem", margin: "0.25rem 0 0", fontFamily: "Outfit, sans-serif" }}>
                          {fieldErrors.currentPincode}
                        </p>
                      )}
                    </div>
                    
                  </div>
                  <div ref={stateDropdownRef} style={{ flex: 2, position: "relative" }}>
                      <span style={labelStyle}>State</span>
                      <div
                        onClick={() => { if (!sameAsPermanent) { setFieldErrors((p) => { const n = { ...p }; delete n.currentState; return n; }); setStateOpen((o) => !o); } }}
                        style={{
                          ...inputStyle,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: sameAsPermanent ? "default" : "pointer",
                          background: sameAsPermanent ? "#f5f5f5" : "#fff",
                          color: form.currentState ? "#333" : "#aaa",
                          userSelect: "none",
                          padding: "0.72rem 0.5rem",
                          borderColor: fieldErrors.currentState ? "#d32028" : "#e0e0e0",
                        }}
                      >
                        <span style={{ fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {form.currentState || "State"}
                        </span>
                        <svg
                          width="12" height="12" viewBox="0 0 24 24" fill="none"
                          style={{ transform: stateOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}
                        >
                          <path d="M6 9L12 15L18 9" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>

                      {stateOpen && (
                        <div style={{
                          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                          background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100,
                          minWidth: "180px",
                        }}>
                          <div style={{ padding: "0.5rem 0.7rem", borderBottom: "1px solid #f0f0f0" }}>
                            <input
                              ref={stateSearchRef}
                              autoFocus
                              value={stateSearch}
                              onChange={(e) => setStateSearch(e.target.value)}
                              placeholder="Search state..."
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: "100%", padding: "0.45rem 0.7rem",
                                border: "1px solid #e0e0e0", borderRadius: "6px",
                                fontSize: "0.83rem", outline: "none",
                                fontFamily: "Outfit, sans-serif", color: "#333",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                          <div style={{ maxHeight: "190px", overflowY: "auto" }}>
                            {INDIAN_STATES.filter((s) =>
                              s.toLowerCase().includes(stateSearch.toLowerCase())
                            ).map((s) => (
                              <div
                                key={s}
                                onClick={() => { handleChange("currentState", s); setStateOpen(false); setStateSearch(""); }}
                                style={{
                                  padding: "0.6rem 0.9rem",
                                  fontSize: "0.85rem",
                                  color: form.currentState === s ? "#d32028" : "#333",
                                  fontWeight: form.currentState === s ? 700 : 400,
                                  background: form.currentState === s ? "#fde8e8" : "transparent",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => { if (form.currentState !== s) (e.currentTarget as HTMLDivElement).style.background = "#f9f9f9"; }}
                                onMouseLeave={(e) => { if (form.currentState !== s) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
                              >
                                {s}
                              </div>
                            ))}
                            {INDIAN_STATES.filter((s) => s.toLowerCase().includes(stateSearch.toLowerCase())).length === 0 && (
                              <div style={{ padding: "0.7rem 0.9rem", fontSize: "0.83rem", color: "#bbb", textAlign: "center" }}>
                                No results
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {fieldErrors.currentState && (
                        <p style={{ color: "#d32028", fontSize: "0.75rem", margin: "0.25rem 0 0", fontFamily: "Outfit, sans-serif" }}>
                          {fieldErrors.currentState}
                        </p>
                      )}
                    </div>
                </div>
              </div>

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem", marginBottom: 0 }}>
                  {error}
                </p>
              )}
              {loading && <Loader />}
            </div>

            {/* ── Confirm & Continue ── */}
            <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center",justifyContent:'center', gap: "0.6rem" }}>
              <button
                onClick={handleNext}
                disabled={loading}
                style={{
                  width: "70%",
                  padding: "0.85rem 1.5rem",
                  background: loading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                Confirm & Continue
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/pan-upload", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityAddressDetails;
